/**
 * Utility functions for handling date conversions between local timezone and UTC
 */

/**
 * Converts a Date object to a local datetime-local input string (YYYY-MM-DDTHH:mm)
 * @param date - The Date object to convert
 * @returns String formatted for datetime-local input
 */
export const dateToLocalDateTimeString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Converts a local datetime-local input string to a Date object
 * @param dateTimeString - String in format YYYY-MM-DDTHH:mm
 * @returns Date object in local timezone
 */
export const localDateTimeStringToDate = (dateTimeString: string): Date => {
    return new Date(dateTimeString);
};

/**
 * Converts a Date object to UTC ISO string for backend submission
 * @param date - The Date object to convert
 * @returns UTC ISO string
 */
export const dateToUTCString = (date: Date): string => {
    return date.toISOString();
};

/**
 * Converts a UTC ISO string from backend to a Date object
 * @param utcString - UTC ISO string from backend
 * @returns Date object
 */
export const utcStringToDate = (utcString: string): Date => {
    console.log(utcString)
    return new Date(utcString);
};

/**
 * Converts form data with Date objects to a format suitable for API submission
 * This converts any Date objects in expires_at fields to UTC ISO strings
 * @param formData - Form data with Date objects
 * @returns Form data with UTC ISO strings
 */
export const prepareFormDataForSubmission = <T extends Record<string, any>>(formData: T): any => {
    const prepared = { ...formData };

    // Handle variations array
    if ('variations' in prepared && Array.isArray(prepared.variations)) {
        (prepared as any).variations = prepared.variations.map((variation: any) => ({
            ...variation,
            expires_at: variation.expires_at instanceof Date
                ? dateToUTCString(variation.expires_at)
                : variation.expires_at
        }));
    }

    return prepared;
};

/**
 * Converts form data from API response to use Date objects
 * This converts any UTC ISO strings in expires_at fields to Date objects
 * @param formData - Form data from API with UTC ISO strings
 * @returns Form data with Date objects
 */
export const prepareFormDataFromAPI = <T extends Record<string, any>>(formData: T): any => {
    const prepared = { ...formData };

    // Handle variations array
    if ('variations' in prepared && Array.isArray(prepared.variations)) {
        (prepared as any).variations = prepared.variations.map((variation: any) => ({
            ...variation,
            expires_at: variation.expires_at && typeof variation.expires_at === 'string'
                ? utcStringToDate(variation.expires_at)
                : variation.expires_at
        }));
    }

    return prepared;
};
